import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { LeadFinderTabKey } from '../../type';
import { RootState } from '../../../../store/root-reducer';
import { leadFinderBulkOperationLimit } from '../../../../shared/utils/user-details';
import { getSavedTabBulkOperationLimit } from '../../helpers/helper';

export const useLeadBulkAction = (
  leadsData,
  selectedLeads,
  setSelectedLeads,
  activeTabKey,
) => {
  const leadFinderLimit = leadFinderBulkOperationLimit();
  const savedTabBulkOperationLimit = getSavedTabBulkOperationLimit();
  const [disabledLeads, setDisabledLeads] = useState([]);
  const [isBulkRevealing, setIsBulkRevealing] = useState(false);
  const [isSelectAllIndeterminate, setIsSelectAllIndeterminate] = useState(
    false,
  );

  const [leadCredits, setLeadsCredits] = useState(0);

  const leadCreditsState = useSelector((state: RootState) => state);

  const [deSelectedLeads, setDeSelectedLeads] = useState([]);
  const [selectedAllLeadsCount, setSelectedAllLeadsCount] = useState(0);
  const [isSelectedAllLeads, setIsSelectedAllLeads] = useState(false);

  const [topPeopleLimit, setTopPeopleLimit] = useState(0);

  useEffect(() => {
    setTopPeopleLimit(
      Number(leadCredits) >= leadFinderLimit
        ? leadFinderLimit
        : Number(leadCredits),
    );
  }, [leadCredits]);

  useEffect(() => {
    const isAllSelected =
      leadsData?.profiles?.length > 0 &&
      leadsData?.profiles?.every(
        (lead) =>
          selectedLeads?.some((l) => l?.id === lead?.id) &&
          !disabledLeads?.some((dLead) => dLead?.id === lead?.id),
      );

    const isSomeSelected =
      leadsData?.profiles?.some(
        (lead) =>
          selectedLeads?.some((l) => l?.id === lead?.id) &&
          !disabledLeads?.some((dLead) => dLead?.id === lead?.id),
      ) && !isAllSelected;

    setIsSelectAllIndeterminate(isSomeSelected);
  }, [selectedLeads, disabledLeads, leadsData]);

  const handleCheckUnCheckSingleLeadV2 = (lead) => {
    const isLeadAdded = selectedLeads?.find((l) => l?.id === lead?.id);
    const isLeadDisabled = disabledLeads?.some(
      (dLead) => dLead?.id === lead?.id,
    );
    const isLeadRevealing = lead?.isRevealing;

    if (!isLeadAdded && !isLeadDisabled && !isLeadRevealing) {
      // selecting the lead
      if (isSelectedAllLeads) {
        setDeSelectedLeads((prevState) => [
          ...prevState.filter((currLead) => currLead.id !== lead.id),
        ]);
      }
      setSelectedLeads((prevState) => [...prevState, lead]);
    } else {
      // de-selecting the lead
      if (isSelectedAllLeads) {
        setDeSelectedLeads((prevState) => [...prevState.concat([lead])]);
      }
      setSelectedLeads((prevState) =>
        prevState.filter((l) => l?.id !== lead?.id),
      );
    }
  };

  useEffect(() => {
    const MAX_BULK_ACTION_COUNT =
      activeTabKey === LeadFinderTabKey.PEOPLE_TAB
        ? leadFinderLimit
        : savedTabBulkOperationLimit;

    const revealingLeads =
      leadsData?.profiles?.filter(
        (lead) =>
          lead?.isRevealing &&
          !disabledLeads?.some((dLead) => dLead?.id === lead?.id),
      ) || [];
    const finalCount =
      Math.min(MAX_BULK_ACTION_COUNT, leadsData?.pagination?.total) -
      deSelectedLeads?.length -
      disabledLeads?.length -
      revealingLeads?.length;

    setSelectedAllLeadsCount(finalCount);
  }, [deSelectedLeads, disabledLeads, leadsData]);

  const handleSelectAllBulkActionLead = (leadProfiles = []) => {
    if (isSelectedAllLeads) {
      return;
    }
    const filteredLeadProfiles = leadProfiles?.filter(
      (lead) => !lead?.isRevealing,
    );
    setIsSelectedAllLeads(true);
    setSelectedLeads([...filteredLeadProfiles]);
    setSelectedAllLeadsCount(leadsData.pagination.total);
    setDeSelectedLeads([]);
  };

  const handleClearSelectedAllBulkAction = () => {
    setIsSelectedAllLeads(false);
    setSelectedLeads([]);
    setSelectedAllLeadsCount(0);
    setDeSelectedLeads([]);
  };

  const handleResetStateAfterAnyAction = (leadIds: number[] = []) => {
    if (!leadIds?.length) {
      setIsSelectedAllLeads(false);
      setSelectedLeads([]);
      setSelectedAllLeadsCount(0);
      setDeSelectedLeads([]);
      return;
    }
    setIsSelectedAllLeads(false);
    setDeSelectedLeads([]);
    setSelectedAllLeadsCount(0);
    setSelectedLeads((prevState) => [
      ...prevState.filter((currLead) => !leadIds?.includes(currLead.id)),
    ]);
  };

  const handleSelectAllLead = (leadProfiles = []) => {
    const isAllSelected = leadProfiles?.every(
      (lead) =>
        selectedLeads?.some((l) => l?.id === lead?.id) &&
        !disabledLeads?.some((dLead) => dLead?.id === lead?.id) &&
        !lead?.isRevealing,
    );

    const leadProfileArrIds = new Set(leadProfiles?.map((item) => item?.id));
    const filteredSelectedLeadArr = selectedLeads?.filter(
      (item) =>
        !leadProfileArrIds?.has(item?.id) &&
        !disabledLeads?.some((dLead) => dLead?.id === item?.id) &&
        !item?.isRevealing,
    );

    if (isAllSelected) {
      setSelectedLeads(filteredSelectedLeadArr);
      if (isSelectedAllLeads) {
        setSelectedAllLeadsCount(null);
      }
      return;
    }

    const filteredLeadProfiles = leadProfiles?.filter(
      (lead) =>
        !disabledLeads?.some((dLead) => dLead?.id === lead?.id) &&
        !lead?.isRevealing,
    );
    setSelectedLeads(() => [
      ...filteredSelectedLeadArr,
      ...filteredLeadProfiles,
    ]);

    if (isSelectedAllLeads) {
      setSelectedAllLeadsCount(leadsData.pagination.total);
    }
  };

  useEffect(() => {
    setDisabledLeads(
      leadsData?.profiles?.filter(
        (l) => l?.isRevealed && l?.emails?.length === 0 && !l?.isRevealing,
      ),
    );
  }, [leadsData]);

  return {
    handleCheckUnCheckSingleLead: handleCheckUnCheckSingleLeadV2,
    selectedLeads,
    handleSelectAllLead,
    setSelectedLeads,
    isBulkRevealing,
    setIsBulkRevealing,
    disabledLeads,
    setDisabledLeads,
    isSelectAllIndeterminate,
    selectedAllLeads: false,
    selectedAllLeadsCount,
    setSelectedAllLeadsCount,
    isSelectedAllLeads,
    setIsSelectedAllLeads,
    handleSelectAllBulkActionLead,
    handleClearSelectedAllBulkAction,
    deSelectedLeads,
    handleResetStateAfterAnyAction,
    leadCredits,
    setLeadsCredits,
    leadCreditsState,
    topPeopleLimit,
  };
};
